import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { selectIdentity } from "../../selectors/userSelectors";
import Heading from "../../shared/ui/Typography/Heading";
import Text from "../../shared/ui/Typography/Text";
import Spinner from "../../shared/ui/Loading/Spinner";
import EmptyState from "../../shared/ui/EmptyState/EmptyState";

import { CompanyInvitationWorkflow } from "../../features/companyInvitations";
import { loadCompany } from "../../features/companyInvitations/api/invitations";
import { messageFromError } from "../../shared/api/drfErrors";
import {
  canInviteAdminRole,
  canOrdinaryAdminWrite,
} from "../../features/companyWorkspace/lib/companyLabels";
import {
  sameActorId,
  sameCompanyId,
} from "../../features/companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  useCompanyFocusRevalidation,
} from "../../features/companyWorkspace/lib/companyFocusRevalidation";

// CompanyInvitationCreateScreen — route landing for
// /companies/:companyId/invitations/new. Mounts under MainLayout.
// Pre-gates ACTIVE OWNER/ADMIN on an active company before mounting
// the writable workflow. Actor+company render match required.
// R4-1: same-identity focus revalidation keeps the workflow mounted.
// R5-2: focus during busy is coalesced into one post-settle refresh.

function CompanyInvitationCreateScreen() {
  const { companyId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const requestIdRef = useRef(0);
  const dirtyRef = useRef(false);
  const [screenBusy, setScreenBusy] = useState(false);

  const [company, setCompany] = useState(null);
  const [loadedActorId, setLoadedActorId] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [revalidating, setRevalidating] = useState(false);

  const handleDirtyChange = useCallback((nextDirty) => {
    dirtyRef.current = Boolean(nextDirty);
  }, []);

  const handleBusyChange = useCallback((nextBusy) => {
    setScreenBusy(Boolean(nextBusy));
  }, []);

  const load = useCallback(
    ({ preserveDirty = false } = {}) => {
      if (companyId == null) {
        setCompany(null);
        setLoadedActorId(null);
        setLoadState("not-found");
        setRevalidating(false);
        return Promise.resolve();
      }

      const keepDraft =
        preserveDirty &&
        company != null &&
        loadedActorId != null &&
        sameActorId(loadedActorId, actorId) &&
        sameCompanyId(company.id, companyId);

      const requestId = ++requestIdRef.current;
      if (!keepDraft) {
        setCompany(null);
        setLoadedActorId(null);
        setLoadState("loading");
        setRevalidating(false);
      } else {
        setRevalidating(true);
      }
      setLoadError(null);

      return loadCompany(companyId)
        .then((data) => {
          if (requestIdRef.current !== requestId) return;
          const membership = data?.my_membership
            ? { status: data.my_membership.status, role: data.my_role }
            : data?.my_role
              ? { status: "active", role: data.my_role }
              : null;
          const stillAdmin = canOrdinaryAdminWrite(data, membership);
          if (!stillAdmin) {
            dirtyRef.current = false;
          }
          setCompany(data);
          setLoadedActorId(actorId);
          setLoadState("loaded");
          setLoadError(null);
          setRevalidating(false);
        })
        .catch((err) => {
          if (requestIdRef.current !== requestId) return;
          const status = err && err.response && err.response.status;
          if (keepDraft && status !== 403 && status !== 404) {
            setLoadError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
            return;
          }
          dirtyRef.current = false;
          setCompany(null);
          setLoadedActorId(actorId);
          setRevalidating(false);
          if (status === 403) {
            setLoadState("forbidden");
          } else if (status === 404) {
            setLoadState("not-found");
          } else {
            setLoadError(messageFromError(err));
            setLoadState("error");
          }
        });
    },
    [actorId, company, companyId, loadedActorId],
  );

  useEffect(() => {
    dirtyRef.current = false;
    setScreenBusy(false);
    setRevalidating(false);
    load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: loadState === "loaded" || loadState === "forbidden",
    busy: screenBusy,
    onRevalidate: () => load({ preserveDirty: true }),
  });

  const companyMatchesRoute =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    loadedActorId != null && sameActorId(loadedActorId, actorId);
  const authorityMatches = companyMatchesRoute && actorMatches;
  const membership = authorityMatches
    ? company?.my_membership
      ? {
          status: company.my_membership.status,
          role: company.my_role,
        }
      : company?.my_role
        ? { status: "active", role: company.my_role }
        : null
    : null;
  const isAdmin =
    authorityMatches && canOrdinaryAdminWrite(company, membership);
  const canInviteAdmin =
    authorityMatches && canInviteAdminRole(company, membership);
  const canMountPrivateForm =
    loadState === "loaded" && authorityMatches && isAdmin;
  const awaitingAuthority =
    (loadState === "loading" && !canMountPrivateForm) ||
    (loadState === "loaded" && company && !authorityMatches);
  const cancelHref = companyId
    ? `/companies/${companyId}/invitations`
    : "/companies/mine";

  return (
    <section
      className="feature-company-invitation"
      id="Csection"
      tabIndex="-1"
    >
      <div className="container">
        <header className="feature-company-invitation__header">
          <Heading level={1} variant="page">
            Запрошення до компанії
          </Heading>
          <Text as="p" size="sm" tone="secondary">
            Створіть запрошення за електронною адресою. Email-розсилка
            вимкнена — після створення можна скопіювати посилання.
          </Text>
        </header>

        {awaitingAuthority ? (
          <div className="feature-company-invitation__state">
            <Spinner size="lg" label="Завантаження компанії" />
          </div>
        ) : null}

        {loadState === "forbidden" ? (
          <div className="feature-company-invitation__state">
            <EmptyState
              icon="fa-solid fa-lock"
              title="Немає доступу"
              description="Запрошувати нових учасників може лише власник або керівник активної компанії."
              size="md"
            />
          </div>
        ) : null}

        {loadState === "not-found" ? (
          <div className="feature-company-invitation__state">
            <EmptyState
              icon="fa-solid fa-magnifying-glass"
              title="Компанію не знайдено"
              description="Перевірте адресу сторінки або поверніться до списку компаній."
              size="md"
            />
          </div>
        ) : null}

        {loadState === "error" ? (
          <div className="feature-company-invitation__state">
            <EmptyState
              icon="fa-solid fa-triangle-exclamation"
              title="Не вдалося завантажити компанію"
              description={loadError || "Спробуйте оновити сторінку."}
              size="md"
            />
          </div>
        ) : null}

        {loadState === "loaded" && authorityMatches && !isAdmin ? (
          <div className="feature-company-invitation__state">
            <EmptyState
              icon="fa-solid fa-lock"
              title="Немає доступу"
              description="Запрошувати нових учасників може лише власник або керівник активної компанії."
              size="md"
            />
          </div>
        ) : null}

        {canMountPrivateForm && loadError ? (
          <p className="feature-company-workspace__error" role="alert">
            {loadError}
          </p>
        ) : null}

        {canMountPrivateForm ? (
          <CompanyInvitationWorkflow
            key={`${companyId}:${actorId}:${canInviteAdmin ? "owner" : "admin"}`}
            companyId={companyId}
            companyName={company && company.name}
            onCancelHref={cancelHref}
            canInviteAdmin={canInviteAdmin}
            onDirtyChange={handleDirtyChange}
            onBusyChange={handleBusyChange}
            writesSuspended={revalidating}
          />
        ) : null}
      </div>
    </section>
  );
}

export default CompanyInvitationCreateScreen;
